import { omit } from "lodash-es";
import { useState } from "react";
import { useSupabaseCallback } from "../../../server/supabase/hooks";
import { SupabaseAccountPersonFieldService } from "../server/supabase-account-person-field-service";
import {
  SupabaseEventService,
  SupabaseScheduleEventService,
} from "../server/supabase-event-service";
import type { Event } from "../types";

type ErrorInfo = {
  error?: string;
  validationError?: string;
};

export function useSaveEvent({
  onChange,
  reset,
}: {
  onChange: (event: Event) => void;
  reset: () => void;
}): { error: ErrorInfo; save: (val: Event) => Promise<void> } {
  const [error, setError] = useState<ErrorInfo>({});

  return {
    error,
    save: useSupabaseCallback(
      async ({ supabase }, values: Event) => {
        if (!values.title) throw new Error("Title expected");

        values.key = values.title.trim().toLowerCase().replace(/\W+/g, "_");

        const {
          data: dataSchedule,
          error: dataError,
          status,
        } = await new SupabaseScheduleEventService(supabase).upsert({
          id: values.id,
          event_key: values.key,
          title: values.title,
        });
        if (status === 409) {
          return setError((prev) => ({
            ...prev,
            validationError: `There is already an event called '${values.title}': please use another title`,
          }));
        }

        if (dataError || !dataSchedule) {
          return setError((prev) => ({ ...prev, error: dataError?.message }));
        }

        if (values.number_of_repeats === undefined) {
          return setError((prev) => ({
            ...prev,
            error: "Number of repeats is required",
          }));
        }

        const event = omit(
          {
            ...values,
            repeat: values.repeat === "never" ? null : values.repeat,
            number_of_repeats: values.number_of_repeats,
          },
          "title",
        );
        const { data, error: err } = await new SupabaseEventService(supabase).upsert(event);
        if (err || !data) {
          return setError((prev) => ({ ...prev, error: err?.message }));
        }
        await new SupabaseAccountPersonFieldService(supabase).insert({
          key: `event_${values.key}`,
          display_name: values.title,
          merge_sample: "December 25th",
          connection_merge_sample: "December 25th",
          merge_field: true,
          connection_merge_field: false,
          channel_merge_field: true,
          segmentation_field: false,
          field_type: "DATE",
          allow_initial: true,
          allow_repeats: values.repeat !== "never",
        });
        onChange({ ...data, title: dataSchedule.title, repeat: values.repeat });
        reset();
      },
      [onChange, reset],
    ),
  };
}
